import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Alert,
  IconButton,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SchoolIcon from '@mui/icons-material/School';
import { format } from 'date-fns';
import { supabase } from '../../../lib/supabase';
import { Worker } from '../../../types/worker';

interface TrainingManagementProps {
  farmId: string;
  workers: Worker[];
}

interface Training {
  id: string;
  workerId: string;
  certificationName: string;
  provider: string;
  completionDate: string;
  expiryDate: string;
  status: string;
}

const TrainingManagement: React.FC<TrainingManagementProps> = ({ farmId, workers }) => {
  const [trainings, setTrainings] = useState<Training[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  
  const [newTraining, setNewTraining] = useState<Partial<Training>>({
    status: 'active'
  });

  const fetchTrainings = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('worker_training')
        .select('*')
        .eq('farm_id', farmId)
        .order('completion_date', { ascending: false });

      if (error) throw error;

      const mapped = (data || []).map((t: any) => ({
        id: t.id,
        workerId: t.worker_id,
        certificationName: t.certification_name,
        provider: t.provider,
        completionDate: t.completion_date,
        expiryDate: t.expiry_date,
        status: t.status
      }));

      setTrainings(mapped);
    } catch (err: any) {
      console.error('Error fetching training:', err);
      setError('Failed to load training records');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (farmId) fetchTrainings();
  }, [farmId]);

  const handleSubmit = async () => {
    try {
      if (!newTraining.workerId || !newTraining.certificationName) {
        setError('Please fill in required fields');
        return;
      }

      const { error } = await supabase
        .from('worker_training')
        .insert([{
          farm_id: farmId,
          worker_id: newTraining.workerId,
          certification_name: newTraining.certificationName,
          provider: newTraining.provider,
          completion_date: newTraining.completionDate,
          expiry_date: newTraining.expiryDate,
          status: newTraining.status
        }]);

      if (error) throw error;
      
      setOpenDialog(false);
      setNewTraining({ status: 'active' });
      await fetchTrainings();
    } catch (err: any) {
      console.error('Error adding training:', err);
      setError('Failed to add training record');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('worker_training').delete().eq('id', id);
      if (error) throw error;
      await fetchTrainings();
    } catch (err) {
      console.error('Error deleting training:', err);
    }
  };

  const getWorkerName = (id: string) => {
    return workers.find(w => w.id === id)?.name || 'Unknown';
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Training & Certifications</Typography>
        <Button 
          variant="contained" 
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
        >
          Add Record
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      <Card variant="outlined">
        <CardContent>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Worker</TableCell>
                  <TableCell>Certification</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Completed</TableCell>
                  <TableCell>Expires</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trainings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No training records found</TableCell>
                  </TableRow>
                ) : (
                  trainings.map((t) => (
                    <TableRow key={t.id}>
                      <TableCell>{getWorkerName(t.workerId)}</TableCell>
                      <TableCell sx={{ fontWeight: 'medium' }}>{t.certificationName}</TableCell>
                      <TableCell>{t.provider || '-'}</TableCell>
                      <TableCell>{t.completionDate ? format(new Date(t.completionDate), 'MMM d, yyyy') : '-'}</TableCell>
                      <TableCell>{t.expiryDate ? format(new Date(t.expiryDate), 'MMM d, yyyy') : '-'}</TableCell>
                      <TableCell>
                        <Chip 
                          label={t.status} 
                          size="small" 
                          color={t.status === 'active' ? 'success' : 'warning'}
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleDelete(t.id)}>
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Training Record</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              label="Worker"
              fullWidth
              value={newTraining.workerId || ''}
              onChange={(e) => setNewTraining({ ...newTraining, workerId: e.target.value })}
            >
              {workers.map((worker) => (
                <MenuItem key={worker.id} value={worker.id}>{worker.name}</MenuItem>
              ))}
            </TextField>

            <TextField
              label="Certification Name"
              fullWidth
              value={newTraining.certificationName || ''}
              onChange={(e) => setNewTraining({ ...newTraining, certificationName: e.target.value })}
            />

            <TextField
              label="Provider / Institution"
              fullWidth
              value={newTraining.provider || ''}
              onChange={(e) => setNewTraining({ ...newTraining, provider: e.target.value })}
            />

            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                type="date"
                label="Completion Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newTraining.completionDate || ''}
                onChange={(e) => setNewTraining({ ...newTraining, completionDate: e.target.value })}
              />
              <TextField
                type="date"
                label="Expiry Date"
                fullWidth
                InputLabelProps={{ shrink: true }}
                value={newTraining.expiryDate || ''}
                onChange={(e) => setNewTraining({ ...newTraining, expiryDate: e.target.value })}
              />
            </Box>

            <TextField
              select
              label="Status"
              fullWidth
              value={newTraining.status || 'active'}
              onChange={(e) => setNewTraining({ ...newTraining, status: e.target.value })}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained">Save Record</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TrainingManagement;
